import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';

// Dashboard popup when the office marks one of this therapist's sessions as a
// debt: the therapist acknowledges the outstanding balance. Persistent — it
// returns every dashboard visit until acknowledged (the ack is server-side).
// Replaced the old "Debt Notification" admin message (2026-09-01).

const fmtMoney = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
function fmtDate(d) {
  if (!d) return '';
  const datePart = String(d).split('T')[0];
  const [y, m, day] = datePart.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function DebtAckPopup() {
  const [debts, setDebts] = useState([]);
  const [open, setOpen] = useState(false);
  const [acking, setAcking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    API.get('/therapist/debt-acknowledgments')
      .then((res) => {
        if (!active) return;
        if (Array.isArray(res.data) && res.data.length > 0) {
          setDebts(res.data);
          setOpen(true);
        }
      })
      .catch(() => { /* best-effort */ });
    return () => { active = false; };
  }, []);

  if (!open) return null;

  const acknowledge = async (thenNavigate) => {
    setAcking(true);
    try {
      await API.post('/therapist/debt-acknowledgments/ack', { ids: debts.map((d) => d.id) });
      setOpen(false);
      if (thenNavigate) navigate('/therapist/debt');
    } catch (err) {
      console.error('Error acknowledging debts:', err);
      setAcking(false);
      alert('Error saving your acknowledgment — please try again.');
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={boxStyle}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 32 }}>💙</div>
          <h2 style={{ margin: '4px 0 2px', fontSize: 20 }}>
            {debts.length === 1 ? 'A session balance is outstanding' : 'Some session balances are outstanding'}
          </h2>
          <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            The office wasn't able to collect payment for{' '}
            {debts.length === 1 ? 'this session' : 'these sessions'}, so
            {debts.length === 1 ? " it's" : " they're"} now tracked as
            {debts.length === 1 ? ' a debt' : ' debts'}.
          </div>
        </div>

        {debts.map((d) => (
          <div key={d.id} style={rowStyle}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{d.client_name}</div>
              <div style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>
                {d.service_type} · {fmtDate(d.session_date)}
              </div>
            </div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtMoney(d.amount)}</div>
          </div>
        ))}

        <div style={{ fontSize: 13.5, color: 'var(--text-strong)', margin: '10px 2px 0', lineHeight: 1.5 }}>
          No action needed right now — the office keeps following up, and you can
          check the status anytime under <strong>My Sessions → Debt Sessions</strong>.
          Acknowledging just confirms you've seen this.
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button onClick={() => acknowledge(true)} disabled={acking} style={secondaryBtnStyle}>
            Acknowledge &amp; view debts
          </button>
          <button onClick={() => acknowledge(false)} disabled={acking} style={{ ...ctaStyle, opacity: acking ? 0.6 : 1 }}>
            {acking ? 'Saving…' : 'I acknowledge'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1095, padding: 16,
};
const boxStyle = {
  background: 'var(--white)', borderRadius: 16, padding: '20px 22px', width: '100%',
  maxWidth: 480, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
};
const rowStyle = {
  display: 'flex', gap: 12, alignItems: 'center', padding: '9px 12px', margin: '6px 0',
  background: '#fde8e8', border: '1px solid #f5c2c2', borderRadius: 10,
};
const ctaStyle = {
  flex: 1, background: 'var(--primary-color)', color: '#fff', border: 'none',
  borderRadius: 10, padding: '11px 14px', fontWeight: 700, fontSize: 14.5, cursor: 'pointer',
};
const secondaryBtnStyle = {
  flex: 1, background: 'var(--surface-muted)', color: 'var(--text-color)', border: 'none',
  borderRadius: 10, padding: '11px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
